<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title :class="{ 'hidden-sm-and-down': showSearch }">
        Student Leader Training Blogs
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-row v-if="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          flat
          hide-details
          single-line
          label="Search news"
          v-model="filter.search"
          @input="search"
        >
        </v-text-field>
      </v-row>
      <v-btn class="ml-5" icon @click="showSearch = !showSearch">
        <v-icon>{{ icons.search }}</v-icon>
      </v-btn>
    </v-app-bar>
    <div class="ma-4" id="news-list">
      <v-btn
        :to="{ name: 'training.blogs.create' }"
        color="primary"
        class="mb-4"
      >
        Create blog
      </v-btn>
      <v-simple-table>
        <thead>
          <tr>
            <th class="text-left">Title</th>
            <th width="40%" class="text-left">Subtitle</th>
            <th class="text-left">Created by</th>
            <th class="text-left">Created at</th>
            <th><span></span></th>
          </tr>
        </thead>
        <tbody>
          <template v-if="blogs.data.length">
            <tr v-for="article in blogs.data" :key="article.id">
              <td class="py-2">
                <text-highlight :queries="filter.search">
                  {{ article.title }}
                </text-highlight>
              </td>
              <td class="py-2">
                <text-highlight :queries="filter.search">
                  {{ article.subtitle }}
                </text-highlight>
              </td>
              <td>{{ article.created_by.full_name }}</td>
              <td>{{ article.createdAt }}</td>
              <td class="text-right">
                <v-menu>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="view(article)">
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="edit(article)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="!article.published_at"
                      @click="publish(article)"
                    >
                      <v-list-item-title>Publish</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else @click="publish(article, false)">
                      <v-list-item-title>Unpublish</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="archive(article)">
                      <v-list-item-title>Archive</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td class="text-center headline grey--text py-10" colspan="6">
              No blogs available.
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-overlay absolute :value="loading" opacity="0.1">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-overlay>
    </div>
    <div v-if="paginationLength > 1" class="text-center mt-4">
      <v-pagination
        @input="pageChanged"
        :value="blogs.meta.current_page"
        :length="paginationLength"
        :total-visible="blogs.meta.per_page"
        circle
      >
      </v-pagination>
    </div>
  </div>
</template>

<script>
import { mdiDotsVertical } from '@mdi/js'
import debounce from 'lodash/debounce'
import TextHighlight from 'vue-text-highlight'
import Blog from '@/models/Blog'
import env from '@/services/env'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import * as BlogType from '@/utils/consts/BlogType'

export default {
  name: 'TrainingBlogPage',

  components: {
    TextHighlight,
    AppBarNavIcon
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      filter: {
        search: ''
      },
      icons: {
        settings: mdiDotsVertical
      },
      blogs: {
        data: [],
        meta: {
          current_page: 1
        }
      }
    }
  },

  computed: {
    paginationLength() {
      return this.blogs.meta.last_page
    }
  },

  methods: {
    getBlogs() {
      this.loading = true
      return Blog.include('created_by')
        .where('search', this.filter.search)
        .where('type', BlogType.TRAINING)
        .page(parseInt(this.blogs.meta.current_page))
        .limit(10)
        .get()
        .then(response => {
          this.blogs = response
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },

    pageChanged(page) {
      this.blogs.meta.current_page = page
      this.getBlogs()
    },

    search: debounce(function() {
      this.getBlogs()
    }, 600),

    view(article) {
      window.open(`${env.appUrl}/training/blogs/${article.slug}`)
    },

    edit(article) {
      this.$router.push({
        name: 'training.blogs.edit',
        params: { id: article.id }
      })
    },

    publish(article, publish = true) {
      this.$api
        .post(`/blogs/${article.id}/publish`, {
          publish
        })
        .then(this.getBlogs)
        .then(() => {
          this.$toast(
            `Blog successfully ${publish ? 'published' : 'unpublished'}!`,
            {
              color: 'green'
            }
          )
        })
    },

    archive(article) {
      this.$confirm(
        `Are you sure you want to archive this blog "${article.title}"?`,
        {
          color: 'primary',
          title: 'Archive blog'
        }
      ).then(confirm => {
        if (confirm) {
          article.delete().then(response => {
            this.blogs.meta.current_page = 1
            this.getBlogs()
            this.$toast('Blog successfully deleted!', {
              color: 'green'
            })
          })
        }
      })
    }
  },

  created() {
    this.getBlogs()
  }
}
</script>
